/**
 * Stores > Store checklist at /stores/hq/checklist.
 *
 * HQ edits the opening, midday, and closing checklist items store managers
 * check off in Today, without filing an IT ticket for a wording change. Three
 * sections, one per phase, each its own small grid: add an item (label +
 * optional position), edit a label inline, reorder with up/down (no
 * drag-and-drop), and retire an item (soft-delete via active=false) with a
 * confirm. Retired items are hidden by default behind "Show retired" and can
 * be reactivated from there.
 *
 * Gated by the stores.checklist access slug (binary access-grant model, spec
 * 00_access-binary-grants.md). It is its own slug, deliberately distinct from
 * stores.users, so store ops can edit checklist wording without gaining the
 * ability to assign accounts to stores. The api's three checklist routes demand
 * the same slug (tools/stores/src/routes.ts). Holding the slug confers full
 * access to the tool; super-admins bypass the check. There is no delete
 * endpoint for a checklist item; retiring sets active=false and reactivating
 * sets it back to true, both via the same PATCH.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { Plus, ArrowUp, ArrowDown, Ban, RotateCcw } from 'lucide-react';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { gridTheadClassName, Badge } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import { hasAccessSlug } from '@/lib/toolVisibility';
import {
  useChecklistItems,
  useCreateChecklistItem,
  useUpdateChecklistItem,
  isNotAvailable,
  type ChecklistItem,
  type ChecklistPhase,
} from '@/lib/useStores';
import { StoresHeader, AccessDeniedCard, NotAvailableCard, ErrorCard } from './storesShared';

const PHASES: { key: ChecklistPhase; title: string }[] = [
  { key: 'open', title: 'Opening' },
  { key: 'midday', title: 'Midday' },
  { key: 'close', title: 'Closing' },
];

const inputClass =
  'rounded-md border border-amp-line bg-amp-white px-2.5 py-1.5 text-sm text-amp-ink ' +
  'focus:border-amp-olive focus:outline-none dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink';

function sortItems(items: ChecklistItem[]): ChecklistItem[] {
  return [...items].sort((a, b) => a.sort - b.sort || a.id - b.id);
}

export function StoreChecklist() {
  const { user } = useSession();
  const canView = hasAccessSlug(user?.permissions, 'stores.checklist', user?.superAdmin);
  const [showRetired, setShowRetired] = useState(false);

  const itemsQ = useChecklistItems();

  if (user && !canView) return <AccessDeniedCard what="Store checklist administration" />;
  if (isNotAvailable(itemsQ.error)) {
    return (
      <div>
        <StoresHeader title="Store checklist" />
        <NotAvailableCard what="Store checklist administration" />
      </div>
    );
  }

  const allItems = itemsQ.data?.items ?? [];

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <StoresHeader
        title="Store checklist"
        subtitle="Edit the opening, midday, and closing checklist items store managers see in Today."
        actions={
          <label className="flex items-center gap-1.5 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <input
              type="checkbox"
              checked={showRetired}
              onChange={(e) => setShowRetired(e.target.checked)}
            />
            Show retired
          </label>
        }
      />
      {itemsQ.error && !isNotAvailable(itemsQ.error) && (
        <div className="mb-3">
          <ErrorCard message={itemsQ.error.message} />
        </div>
      )}
      <div className="flex flex-col gap-6">
        {PHASES.map((p) => (
          <PhaseSection
            key={p.key}
            phase={p.key}
            title={p.title}
            allItems={allItems}
            showRetired={showRetired}
            loading={itemsQ.isLoading}
          />
        ))}
      </div>
    </div>
  );
}

function PhaseSection({
  phase,
  title,
  allItems,
  showRetired,
  loading,
}: {
  phase: ChecklistPhase;
  title: string;
  allItems: ChecklistItem[];
  showRetired: boolean;
  loading: boolean;
}) {
  const createM = useCreateChecklistItem();
  const moveM = useUpdateChecklistItem();
  const [label, setLabel] = useState('');
  const [position, setPosition] = useState<number | ''>('');

  const phaseAll = allItems.filter((i) => i.phase === phase);
  const phaseItems = sortItems(phaseAll.filter((i) => showRetired || i.active));
  const nextSort = phaseAll.reduce((max, i) => Math.max(max, i.sort), 0) + 1;

  const move = (index: number, direction: -1 | 1) => {
    const current = phaseItems[index];
    const other = phaseItems[index + direction];
    if (!current || !other) return;
    moveM.mutate({ id: current.id, sort: other.sort });
    moveM.mutate({ id: other.id, sort: current.sort });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = label.trim();
    if (!trimmed) return;
    createM.mutate(
      { phase, label: trimmed, sort: position === '' ? nextSort : position },
      {
        onSuccess: () => {
          setLabel('');
          setPosition('');
        },
      },
    );
  };

  const addForm = (
    <form onSubmit={submit} className="flex flex-wrap items-center gap-2">
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="New checklist item"
        aria-label={`New ${title} item label`}
        required
        className={`${inputClass} min-w-[220px] flex-1`}
      />
      <input
        type="number"
        value={position}
        onChange={(e) => setPosition(e.target.value === '' ? '' : Number(e.target.value))}
        placeholder={`Position (default ${nextSort})`}
        aria-label={`Position for new ${title} item`}
        className={`${inputClass} w-[170px]`}
      />
      <button
        type="submit"
        disabled={createM.isPending || !label.trim()}
        className="inline-flex items-center gap-1.5 rounded-md bg-amp-olive px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50 dark:bg-amp-olive-dark"
      >
        <Plus className="h-4 w-4" aria-hidden /> Add
      </button>
    </form>
  );

  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {title}
      </h2>
      {createM.error && (
        <div className="mb-2">
          <ErrorCard message={createM.error.message} />
        </div>
      )}
      <DataGridShell total={phaseItems.length} loaded={phaseItems.length} countNoun="items" toolbar={addForm}>
        <table className="min-w-full text-sm">
          <thead className={gridTheadClassName}>
            <tr>
              <th className="px-3 py-2 w-[80px] text-left font-medium">Order</th>
              <th className="px-3 py-2 text-left font-medium">Item</th>
              <th className="px-3 py-2 w-[110px] text-left font-medium">Status</th>
              <th className="px-3 py-2 w-[140px] text-left font-medium">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {loading ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  Loading items...
                </td>
              </tr>
            ) : phaseItems.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                  No {title.toLowerCase()} items yet. Add the first one below.
                </td>
              </tr>
            ) : (
              phaseItems.map((item, index) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  count={phaseItems.length}
                  onMove={move}
                  moveBusy={moveM.isPending}
                />
              ))
            )}
          </tbody>
        </table>
      </DataGridShell>
    </div>
  );
}

function ItemRow({
  item,
  index,
  count,
  onMove,
  moveBusy,
}: {
  item: ChecklistItem;
  index: number;
  count: number;
  onMove: (index: number, direction: -1 | 1) => void;
  moveBusy: boolean;
}) {
  const updateM = useUpdateChecklistItem();
  const [draft, setDraft] = useState(item.label);

  const commitLabel = () => {
    const trimmed = draft.trim();
    if (!trimmed || trimmed === item.label) {
      setDraft(item.label);
      return;
    }
    updateM.mutate({ id: item.id, label: trimmed });
  };

  const retire = () => {
    if (
      !window.confirm(
        `Retire "${item.label}"? It stops showing in Today. Reactivate it later from Show retired.`,
      )
    )
      return;
    updateM.mutate({ id: item.id, active: false });
  };

  const reactivate = () => updateM.mutate({ id: item.id, active: true });

  return (
    <tr className={item.active ? undefined : 'opacity-60'}>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onMove(index, -1)}
            disabled={index === 0 || moveBusy}
            aria-label={`Move ${item.label} up`}
            className="rounded border border-amp-line p-1 text-amp-ink disabled:opacity-30 hover:border-amp-olive dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            <ArrowUp className="h-3.5 w-3.5" aria-hidden />
          </button>
          <button
            type="button"
            onClick={() => onMove(index, 1)}
            disabled={index === count - 1 || moveBusy}
            aria-label={`Move ${item.label} down`}
            className="rounded border border-amp-line p-1 text-amp-ink disabled:opacity-30 hover:border-amp-olive dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            <ArrowDown className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      </td>
      <td className="px-3 py-2">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitLabel}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          disabled={updateM.isPending}
          aria-label={`Label for ${item.label}`}
          className="w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-sm text-amp-ink focus:border-amp-olive focus:bg-amp-white focus:outline-none dark:text-amp-dark-ink dark:focus:bg-amp-dark-surface"
        />
      </td>
      <td className="px-3 py-2">
        <Badge tone={item.active ? 'success' : 'neutral'}>{item.active ? 'Active' : 'Retired'}</Badge>
      </td>
      <td className="px-3 py-2">
        {item.active ? (
          <button
            type="button"
            onClick={retire}
            disabled={updateM.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-amp-line px-2 py-1 text-xs text-red-700 hover:border-red-400 disabled:opacity-40 dark:border-amp-dark-line dark:text-red-400"
          >
            <Ban className="h-3.5 w-3.5" aria-hidden /> Retire
          </button>
        ) : (
          <button
            type="button"
            onClick={reactivate}
            disabled={updateM.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-amp-line px-2 py-1 text-xs text-amp-ink hover:border-amp-olive disabled:opacity-40 dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Reactivate
          </button>
        )}
        {updateM.error && (
          <p className="mt-1 text-xs text-red-700 dark:text-red-400">{updateM.error.message}</p>
        )}
      </td>
    </tr>
  );
}
